import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import classnames from 'classnames';
import { Spin, Typography } from 'antd';
import { useThemeVarsClass, Select, Tooltip } from '@bettercomp/ui-components';
import { useReachableAccounts } from 'hooks/useReachableAccounts';
import { useAccountSwitch } from 'hooks/useAccountSwitch';
import { broadcastTenancySwitchAndReload } from 'service/tenancyBroadcast';
import cn from './styles.module.scss';

/**
 * Account switcher for an AMS session (AMS scenario 14 / card 170232).
 *
 * Sibling of AmsWorkspaceSwitcher rather than a mode inside it, for the same reason that one is a
 * sibling of LegacyTenancySwitcher: the two answer different questions. "Which account am I in" is
 * decided by a cross-account grant and changes which workspaces exist at all; "which of that
 * account's workspaces am I in" is decided by a workspace grant. Rendered together they read as two
 * steps, which is what they are — pick an account, then pick a workspace within it.
 *
 * The current account comes from the server's `is_current` flag, which the list already carries.
 */
export const AmsAccountSwitcher = () => {
  const { accounts, isLoading, isUnavailable } = useReachableAccounts();
  const { switchAccount, isSwitching } = useAccountSwitch();
  const themeClassName = useThemeVarsClass();

  const [isReloading, setIsReloading] = useState(false);

  const currentAccount = useMemo(() => accounts.find((account) => account.is_current), [accounts]);

  const options = useMemo(
    () =>
      accounts
        .filter((account) => !account.is_current)
        .map((account) => ({ label: account.name, value: account.account_code })),
    [accounts],
  );

  const handleChange = async (accountCode: string) => {
    const result = await switchAccount(accountCode);
    if (result !== 'switched') {
      return;
    }

    // Reload rather than invalidate, and for a stronger reason than the workspace switch: crossing
    // an account boundary changes which workspaces, roles and permissions exist at all, so every
    // cached answer in the page is about the account being left. The reload also re-runs the token
    // exchange, which is what actually lands the session in the new account — the switch call only
    // records the choice, and its returned token is discarded.
    setIsReloading(true);
    broadcastTenancySwitchAndReload();
  };

  // A failed fetch must not read as "you can reach only your own account" — that would silently
  // remove the control from someone holding grants, with nothing on the page saying why.
  if (isUnavailable) {
    return (
      <div className={cn.wrapper}>
        <Tooltip title="Your accounts could not be loaded. Refresh to try again.">
          <Typography.Text type="secondary" data-testid="accountSwitcherUnavailable">
            Accounts unavailable
          </Typography.Text>
        </Tooltip>
      </div>
    );
  }

  // The control exists only when there is somewhere to go. One account is the ordinary case — the
  // list always contains the caller's home account, so a length of one means no grants.
  if (isLoading || accounts.length <= 1) {
    return null;
  }

  const isBusy = isSwitching || isReloading;

  return (
    <>
      <div className={cn.wrapper}>
        <Select
          placeholder={currentAccount?.name}
          value={undefined}
          options={options}
          onChange={handleChange}
          showSearch={{ optionFilterProp: 'label' }}
          variant="filled"
          size="medium"
          className={cn.tenantSelect}
          classNames={{ popup: { root: cn.tenantSelectPopup } }}
          disabled={isBusy}
          data-testid="accountSelector"
        />
      </div>
      {isBusy &&
        createPortal(
          <div className={classnames(themeClassName, cn.switchOverlay)} data-testid="accountSwitchOverlay">
            <Spin size="large" />
          </div>,
          document.body,
        )}
    </>
  );
};
